/* eslint-disable no-console */
/**
 * Importação da classe genérica para requisições em serviços
 */
import GenericCrud from 'services/crud/GenericCrud';
import WFS from 'services/crud/WFS';
import WMS from 'services/crud/WMS';
import Layer from 'services/models/Layer';
import selectedFeaturesStyles from 'services/models/selectedFeaturesStyles.json';

/**
 *  useModules é responsavel por gerenciar os modulos do GeoBahia,
 *  todos os módulos criados na ADM.
 */
import { useModule } from '../Module';


/**
 * Importação da biblioteca leaflet a qual utiliza o OpenStreetMap,
 * é uma biblioteca de mapeamento leve e de codigo aberto, utiliza um banco
 * de dados geografico editável gratuito.
 */
import L from 'leaflet';
import 'leaflet.utm';
import '@leaflet/includes';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import '@leaflet/measure';
import 'leaflet-side-by-side';
/**
 * useCallback é um hook que retorna uma função de retorno
 * de chamada memorizada
 * 
 * useContext usado para forcener uma maneira de passar dados
 * pela arvore de componentes sem ter que passar props manualmente
 * em todos os níveis.
 * 
 * useState é um hook que nos permite rastrear o estado em um
 * componente de função, o estado geralmente se refere a dados ou
 * propriedads que precisam ser rastreador em um aplicativo.
 * 
 * useEffect é um hook que permite executar efeitos colaterais em
 * seus componentes, por exemplo: buscar dados, atualizar diretamente
 * o DOM e cronômetros, ele aceita dois argumentos o segundo é opcional.
 * 
 * useRef permite que persista valores entre renderizações, ele pode
 * ser usado para armazenar um valor mutável que não causa uma nova
 * renderização quando atualizado e pode ser usado para acessar di-
 * retamente um elemento do DOM.
 */
import {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import ReactDOM from 'react-dom';
import AlertConfirm from 'views/MapView/components/ToolBar/components/Analysis/IdentifyFeatureLocation/components/AlertConfirm';

const basemapsCrud = new GenericCrud('mapa-base', {
  post: 'private',
  put: 'private',
  delete: 'private',
});

export const Context = createContext({});

export const Provider = ({ children }) => {
  const [loadingMap, setLoadingMap] = useState(false);
  const [loadingInMap, setLoadingInMap] = useState(false);
  const map = useRef(null);
  const sideBySideControl = useRef(null);
  const [leftLayer, setLeftLayer] = useState(null);
  const [rightLayer, setRightLayer] = useState(null);
  const [layers, setLayers] = useState([]);
  const [basemaps, setBasemaps] = useState([]);

  const { activeModule, getElement } = useModule();

  /**
   * Adiciona mapa base ao mapa
   * @param {*} id
   * @param {*} active
   * @param {*} mapToAdd
   * @returns
   */
  const addBaseMap = useCallback(async (id, active = false) => {
    if (id) {
      try {
        const basemapData = await basemapsCrud.getOne(id);
        const tileLayer = L.tileLayer(basemapData.data.link, {
          attribution: basemapData.data.atribuicao,
          subdomains: basemapData.data.subdominio,
          minZoom: basemapData.data.zoom_minimo,
          maxZoom: basemapData.data.zoom_maximo,
        });
        let baseMap = { ...basemapData.data, layer: tileLayer, active };
        setBasemaps((old) => [...(old || []), baseMap]);
        return baseMap;
      } catch (error) {
        console.error(`Erro ao buscar mapa base id ${id}`, error);
        return null;
      }
    }
  }, []);

  /**
   * Altera mapa base ativo
   * @param {*} id
   */
  const changeBaseMap = useCallback((id) => {
    setBasemaps((old) => {
      old.forEach((b) => {
        if (b.id === id) {
          b.layer.addTo(map.current);
          b.active = true;
        } else {
          b.layer.remove();
          b.active = false;
        }
      });
      return old;
    });
  }, []);

  /**
   * Altera temporalidade do mapa base
   * @param {*} id Id do mapa que vai ser modificado
   * @param {*} link Novo link do mapa base para fazer requisição
   */
  const changeBaseMapDinamic = useCallback((id, link) => {
    setBasemaps((old) => {
      old.forEach((b) => {
        if (b.id === id) {
          b.layer.setUrl(link)
          b.active = true;
        } else {
          b.layer.remove();
          b.active = false;
        }
      });
      return old;
    });
  }, []);

  /* 
    * Função Responsavel por mudar o mes/ano de referencia
    * dinamicamente do mapa base da Planet SCCON
  */
  const changeAttributionDinamic = useCallback((monthYear) => {
    if (!map.current) return;
  
    setTimeout(() => {
      const attributionElement = document.querySelector(".leaflet-control-attribution");
      if (!attributionElement) return;
  
      // Pega a atribuicao direto no html
      let currentAttribution = attributionElement.innerHTML;
  
      // Pega atribuicao do mapa base da SCCON
      const attributionDefault = basemaps.find((item) => item.atribuicao.includes('Planet Labs'))?.atribuicao || '';
  
      // Substitui '(ano)' por monthYear
      const updatedAttribution = attributionDefault.replace(/\(ano\)/, monthYear);
  
      // Mantém o prefixo e junta com a nova atribuicao
      attributionElement.innerHTML = `${currentAttribution.split('|')[0].trim()} | ${updatedAttribution}`;
    }, 100);
  }, [basemaps]);

  /**
   * Altera temporalidade de um dos lados do side by side
   * @param {*} basemap Mapa
   * @param {*} side Lado para referencia do side-by-side(esquerdo ou direito)
   * @param {*} link Novo link do mapa base para fazer  requisição
   */
  const changeSide = useCallback((basemap, side, link) => {
    const layerOptions = {
      attribution: basemap.atribuicao,
      subdomains: basemap.subdominio || [],
      minZoom: basemap.zoom_minimo || 0,
      maxZoom: basemap.zoom_maximo || 18,
    };

    if (side === 'left') {
      // Remover a camada anterior se existir
      if (leftLayer) {
        map.current.removeLayer(leftLayer);
      }

      // Criar e adicionar a nova camada
      const newLeftLayer = L.tileLayer(link, layerOptions);
      newLeftLayer.addTo(map.current);
      setLeftLayer(newLeftLayer);

      // Atualiza a camada do lado esquerdo no controle
      sideBySideControl.current.setLeftLayers(newLeftLayer);
    } else if (side === 'right') {
      // Remover a camada anterior se existir
      if (rightLayer) {
        map.current.removeLayer(rightLayer);
      }

      // Criar e adicionar a nova camada
      const newRightLayer = L.tileLayer(link, layerOptions);
      newRightLayer.addTo(map.current);
      setRightLayer(newRightLayer);

      // Atualiza a camada do lado direito no controle
      sideBySideControl.current.setRightLayers(newRightLayer);
    }
  }, [map, leftLayer, rightLayer]);

  /**
   * Ativa a ferramenta de Side by Side
   * @param {*} basemap Mapabase
   * @param {*} linkLeft Link do mapabase para o lado esquerdo
   * @param {*} linkRight Link do mapabase para o lado direito
   */
  const enableSideBySide = useCallback((basemap, linkLeft, linkRight) => {
    const newLeftLayer = L.tileLayer(linkLeft, {
      attribution: basemap.atribuicao,
      subdomains: basemap.subdominio,
      minZoom: basemap.zoom_minimo,
      maxZoom: basemap.zoom_maximo,
    });

    const newRightLayer = L.tileLayer(linkRight, {
      attribution: basemap.atribuicao,
      subdomains: basemap.subdominio,
      minZoom: basemap.zoom_minimo,
      maxZoom: basemap.zoom_maximo,
    });

    // Remove as camadas anteriores se existirem
    if (leftLayer) { map.current.removeLayer(leftLayer) }
    if (rightLayer) { map.current.removeLayer(rightLayer) }

    // Adiciona as novas camadas
    newLeftLayer.addTo(map.current);
    newRightLayer.addTo(map.current);

    // Atualiza os estados com as novas camadas
    setLeftLayer(newLeftLayer);
    setRightLayer(newRightLayer);

    if (!sideBySideControl.current) {
      sideBySideControl.current = L.control.sideBySide(newLeftLayer, newRightLayer).addTo(map.current);

      const sideBySideContainer = sideBySideControl.current.getContainer();

      sideBySideContainer.addEventListener('pointerdown', () => {
        map.current.dragging.disable();
        map.current.touchZoom.disable();
        map.current.scrollWheelZoom.disable();
      });

      sideBySideContainer.addEventListener('pointerup', () => {
        map.current.dragging.enable();
        map.current.touchZoom.enable();
        map.current.scrollWheelZoom.enable();
      });

      sideBySideContainer.addEventListener('mouseleave', () => {
        map.current.dragging.enable();
        map.current.touchZoom.enable();
        map.current.scrollWheelZoom.enable();
      });

    } else {
      sideBySideControl.current.setLeftLayers(newLeftLayer);
      sideBySideControl.current.setRightLayers(newRightLayer);
    }

  }, [map, leftLayer, rightLayer]);

  /**
   * Desativa o Side By Side
   * Resetando todos os states para o 
   * mapa voltar a funcionar normalmente
   */
  const disableSideBySide = () => {
    // Remover camadas do controle
    if (sideBySideControl.current) {
      sideBySideControl.current.setLeftLayers(null);
      sideBySideControl.current.setRightLayers(null);
    }

    // Remover as camadas do mapa
    if (leftLayer) {
      map.current.removeLayer(leftLayer);
      setLeftLayer(null);
    }
    if (rightLayer) {
      map.current.removeLayer(rightLayer);
      setRightLayer(null);
    }

    // Remover o controle do mapa
    if (sideBySideControl.current) {
      map.current.removeControl(sideBySideControl.current);
      sideBySideControl.current = null;
    }
  };

  /**
   * Reorganiza o z-index das camadas no mapa
   */
  const organizeLayersZIndex = useCallback((layersToOrg) => {
    const countLayers = layersToOrg.length;
    layersToOrg.forEach((l, i) => {
      // Determina valor do zindex de acordo com a posição no array, sendo que os de cima devem sobrepor os de baixo
      const zIndex = countLayers - i + 10;
      l.setZIndex(zIndex);
    });
  }, []);

  /**
   * Reordena a lista de camadas de acordo com o tipo de dado
   * @param {*} layersToSort
   * @param {*} layer
   * @returns
   */
  const sortLayers = useCallback(
    (layersToSort, layer = null) => {
      const sortLayer = (addedLayer, paramLayers) => {
        const layerTypeWeight = {
          Text: 5,
          Point: 4,
          MultiPoint: 4,
          Line: 3,
          LineString: 3,
          MultiLineString: 3,
          Polygon: 2,
          MultiPolygon: 2,
          GeometryCollection: 2,
          Raster: 1,
        };

        const orderedLayers = [];
        let added = false;
        const addedWheight = layerTypeWeight[addedLayer.dataType];
        const addedTitle = `${addedLayer.getTitle()}`.toUpperCase();

        if (paramLayers) {
          paramLayers.forEach((v, i, a) => {
            if (!added) {
              const currentWheight = layerTypeWeight[v.dataType];
              const nextWheight =
                a.length > i + 1 ? layerTypeWeight[a[i + 1].dataType] : null;

              if (addedWheight > currentWheight) {
                orderedLayers.push(addedLayer);
                orderedLayers.push(v);
                added = true;
              } else if (addedWheight === currentWheight) {
                const currentTitle = `${v.getTitle()}`.toUpperCase();

                if (
                  (nextWheight && addedWheight > nextWheight) ||
                  !nextWheight
                ) {
                  orderedLayers.push(
                    addedTitle <= currentTitle ? addedLayer : v
                  );
                  orderedLayers.push(
                    addedTitle <= currentTitle ? v : addedLayer
                  );
                  added = true;
                } else if (nextWheight && addedWheight === nextWheight) {
                  const nextTitle = `${a[i + 1].getTitle()}`.toUpperCase();

                  if (addedTitle <= currentTitle) {
                    orderedLayers.push(addedLayer);
                    orderedLayers.push(v);
                    added = true;
                  } else if (addedTitle < nextTitle) {
                    orderedLayers.push(v);
                    orderedLayers.push(addedLayer);
                    added = true;
                  } else {
                    orderedLayers.push(v);
                  }
                } else {
                  orderedLayers.push(v);
                }
              } else {
                orderedLayers.push(v);
              }
            } else {
              orderedLayers.push(v);
            }
          });
        }
        if (!added) {
          orderedLayers.push(addedLayer);
          added = true;
        }
        return orderedLayers;
      };
      if (layer) {
        const newLayersOfSortLayers = sortLayer(layer, layersToSort);
        organizeLayersZIndex(newLayersOfSortLayers);
        return newLayersOfSortLayers;
      }
      let newLayers = [];
      layersToSort.forEach((l) => {
        newLayers = sortLayer(l, newLayers);
      });
      organizeLayersZIndex(newLayers);
      return newLayers;
    },
    [organizeLayersZIndex]
  );

  /**
   * Adiciona uma nova camada ao mapa
   * @param {*} srcLayer
   * @param {*} mapToAdd
   * @param {*} sort
   * @returns
   */
  const addLayer = useCallback(
    async (srcLayer, mapToAdd = null, sort = true) => {
      mapToAdd = mapToAdd || map.current;
      let layer = new Layer(
        srcLayer.parentId,
        srcLayer.type,
        srcLayer.externalServiceId,
        srcLayer.alias,
        srcLayer.abstract,
        srcLayer.geoserverName,
        srcLayer.geoserverWorkspace,
        srcLayer.additionalProps,
        srcLayer.order
      );
      if (srcLayer.dataType) layer.dataType = srcLayer.dataType;
      if (srcLayer.properties) layer.properties = srcLayer.properties;
      // if (srcLayer.layerStyle.opacity) layer.setLayerStyleOpacity(srcLayer.layerStyle.opacity);
      if (srcLayer.layerStyle) layer.setLayerStyle(srcLayer.layerStyle);
      if (srcLayer.type === 'importada' || srcLayer.type === 'criada')
        layer = srcLayer;

      await layer.loadData();

      // Inicialização da layer
      if (layer.leafletLayer) layer.leafletLayer.remove();
      layer.loadLayer(mapToAdd);

      if (sort && layer) {
        setLayers((old) => sortLayers(old, layer));
      } else if (layer) {
        setLayers((old) => [layer, ...(old || [])]);
      }
      return layer;
    },
    [map, sortLayers]
  );

  /**
   * Adiciona uma nova camada raster ao mapa
   * @param {*} srcLayer
   * @param {*} mapToAdd
   * @param {*} sort
   * @returns
   */
  const addRasterLayer = useCallback(
    async (srcLayer, mapToAdd = null, sort = true) => {
      mapToAdd = mapToAdd || map.current;
      let layer = srcLayer;

      await layer.loadData();

      layer.loadLayer(mapToAdd);

      if (sort && layer) {
        setLayers((old) => sortLayers(old, layer));
      } else if (layer) {
        setLayers((old) => [layer, ...(old || [])]);
      }
      return layer;
    },
    [map, sortLayers]
  );

  /**
   * Remove uma camada do mapa
   * @param {*} layerId
   * @returns
   */
  const removeLayer = useCallback(
    (layerId) => {
      setLayers((old) => {
        const newArrayLayers = old.filter((l) => {
          if (l.id === layerId) {
            l.leafletLayer.remove();
            return false;
          }
          return true;
        });
        organizeLayersZIndex(newArrayLayers);
        return newArrayLayers;
      });
    },
    [organizeLayersZIndex]
  );

  /**
   * Altera status de visibilidade do rótulo da camada
   * @param {*} layerId
   * @param {*} visible
   * @returns
   */
  const changeStatusLabelVisible = useCallback((layerId, visible) => {
    setLayers((old) => {
      old.forEach((l) => {
        if (l.id === layerId) {
          l.setStatusLabelVisible(visible);
        }
      });
      return [...old];
    });
  }, []);

  /**
   * Define coluna selecionada como rótulo
   * @param {*} layerId
   * @param {*} column
   * @returns
   */
  const changeSelectedLabelColumn = useCallback((layerId, column) => {
    setLayers((old) => {
      old.forEach((l) => {
        if (l.id === layerId) {
          l.setSelectedLabelColumn(column);
        }
      });
      return [...old];
    });
  }, []);

  /**
   * Altera opacidade dos rótulos da camada
   * @param {*} layer
   * @param {*} opacity
   * @returns
   */
  const changeLabelOpacity = useCallback((layer, opacity) => {
    // eslint-disable-next-line no-underscore-dangle
    layer.leafletLayer?._layers &&
      layer.leafletLayer.eachLayer((feature) => {
        let tooltipContent = layer.getTooltipContent(feature);
        let tooltipOption = layer.getTooltipOptions(feature);
        feature.unbindTooltip();
        feature.bindTooltip(tooltipContent, {
          ...tooltipOption,
          opacity: feature.options.opacity > 0 ? opacity : 0,
        });
      });
  }, []);

  /**
   * Alterna visibilidade dos rótulos da camada
   * @param {*} layerId
   * @returns
   */
  const configureVisibilityLabel = useCallback(
    (layerId) => {
      setLayers((old) => {
        old.forEach((l) => {
          if (l.id === layerId) {
            let opacity = l.statusLabelVisibility ? 0 : 1;
            changeLabelOpacity(l, opacity);
            l.setStatusLabelVisible(!l.statusLabelVisibility);
          }
        });
        return [...old];
      });
    },
    [changeLabelOpacity]
  );

  /**
   * Torna visível uma camada
   * @param {*} layerId
   * @param {boolean} visibility
   * @returns
   */
  const setVisibilityLayer = useCallback((layerId, visibility = true) => {
    setLayers((old) => {
      old.forEach((l) => {
        if (l.id === layerId) {
          l.setVisible(visibility);
        }
      });
      return [...old];
    });
  }, []);

  const removeClonedHighlightLayer = async (layer) => {
    map.current.eachLayer(function (l) {
      if (l.options.alias === 'clonedLayer' + layer.id) {
        l.remove();
      }
    });
  };

  /**
   * Destaca as feições selecionadas em uma camada específica no mapa.
   * 
   * Esta função obtém os dados das feições selecionadas via uma requisição WFS (Web Feature Service),
   * remove qualquer camada de destaque anterior, e renderiza as feições destacadas no mapa
   * com um estilo personalizado. A renderização é feita em um painel específico usando o canvas do Leaflet.
   * 
   * @param {Object} layer - A camada cujas feições selecionadas serão destacadas.
   * @returns {Promise<void>} - Uma promessa que resolve quando as feições forem destacadas, ou rejeita em caso de erro.
   */

  const highlightSelectedFeature = useCallback(async (layer) => {
    let arrGid = layer.selectedFeatures.map((f) => f.gid);
    let url = `${layer.getFullServiceLink('WFS', null, false)}featureID=${arrGid.toString()}`
    let response;
    try {
      response = await WFS.getFeature(url);

      removeClonedHighlightLayer(layer);

      let layerStyle = selectedFeaturesStyles.find((s) => s.for.includes(layer.dataType));
      let customRender = L.canvas({ pane: 'paneClonedSelectedFeatures' });
      let options = {
        alias: 'clonedLayer' + layer.id,
        pane: 'paneClonedSelectedFeatures',
        renderer: customRender,
        style: layerStyle,
        pointToLayer: function (feature, latlng) {
          return L.circleMarker(latlng, {
            pane: 'paneClonedSelectedFeatures',
            ...layerStyle,
            renderer: customRender,
          });
        },
      };
      let geoJsonLayer = L.geoJSON(response.data, options);
      geoJsonLayer.addTo(map.current);
    } catch (error) {
      console.error(
        `Erro ao destacar feições da camada ${layer.getTitle()}: ${error}`
      );
    }
  }, []);

  /**
   * Altera a opacidade de camadas no mapa   
   * @param {*} layerId
   * @param {*} opacity
   * @param {boolean} transparency
   * @returns
   */
  const changeLayerOpacity = useCallback(
    (layerId, opacity, fillOpacity, transparency = false) => {
      setLayers((old) => {
        old.forEach((l) => {
          if (l.id === layerId) {
            setVisibilityLayer(l.id, Boolean(opacity));
            if (l.getService() === 'WFS' || l.getService() === 'NO') {
              if (l.dataType === 'Raster') {
                l?.leafletLayer?.setOpacity(opacity);
              } else {
                if (opacity === 1 && !transparency) {
                  l.applyFeaturesStyle();
                } else {
                  l.leafletLayer.setStyle({
                    opacity: opacity,
                    // fillOpacity: (l.dataType === "Point" || l.dataType === "MultiPoint") ? (opacity * 1) : (opacity * fillOpacity)
                    fillOpacity: (l.dataType === "Point" || l.dataType === "MultiPoint") ? (opacity * 1) : (fillOpacity)
                  });
                }
              }
            } else {
              if (opacity === 1 && !transparency) {
                l?.leafletLayer?.setOpacity(l.layerStyle.opacity);
              } else {
                l?.leafletLayer?.setOpacity(opacity);
              }
            }

            if (transparency) {
              l.setLayerStyleOpacity(opacity);
              l.setLayerStyleFillOpacity(fillOpacity);
            }
            // Exibe rótulo conforme seu status
            l.statusLabelVisibility && l.selectedLabelColumn && changeLabelOpacity(l, opacity ? 1 : 0);
          }
        });
        return [...old];
      });
    },
    [setVisibilityLayer, changeLabelOpacity]
  );

  /**
   * Oculta uma camada no mapa
   * @param {*} layerId
   * @returns
   */
  const hideLayer = useCallback((layerId) => {
    setLayers((old) => {
      old.forEach((l) => {
        if (l.id === layerId) {
          setVisibilityLayer(l.id, false);
          l.leafletLayer.remove();
          removeClonedHighlightLayer(l);
        }
      });
      return [...old];
    });
  }, [setVisibilityLayer]);

  /**
   * Exibe uma camada que está oculta no mapa
   * @param {*} layerId
   * @returns
   */
  const showLayer = useCallback((layerId) => {
    setLayers((old) => {
      old.forEach((l) => {
        if (l.id === layerId) {
          setVisibilityLayer(l.id, true);
          // Reaplica filtros aplicados sobre a camada ao readiciona-la ao mapa          
          // if (l.leafletLayer.wmsParams?.cql_filter) {  
          //   let cqlFilter = l.leafletLayer.wmsParams?.cql_filter;           
          //   l.loadLayer(map.current, { cache: false }).then((ll) => {                        
          //     ll.leafletLayer.setParams({
          //       cql_filter: `${cqlFilter}`,                
          //     });
          //   });
          // } else {
          l.loadLayer(map.current);
          // }  
          highlightSelectedFeature(l);
        }
      });
      return [...old];
    });
  }, [setVisibilityLayer, highlightSelectedFeature]);

  /**
   * Aplica a visibilidade das camadas recem adicionadas através da importação, com base no attr `visible`
   * @returns
   */
  const applyLayerOpacity = useCallback(() => {
    setLayers((old) => {
      old.forEach((l) => {
        let opacity = l.visible ? l.layerStyle.opacity : 0;
        if (l.getService() === 'WFS' || l.getService() === 'NO') {
          /**
           * TODO A classe layer deverar guardar o valor base da opacidade da linha e
           * de preenchimento, para se calcular a opacidade relativa, e não causar
           * divergências de estilo ao alterar a opacidade
           */
          if (opacity === 1) l.leafletLayer.resetStyle();
          else l.leafletLayer.setStyle({
            opacity,
            fillOpacity: (l.dataType === "Point" || l.dataType === "MultiPoint") ? (l.layerStyle.opacity * 1) : (l.layerStyle.fillOpacity),
          });
        } else {
          l.leafletLayer.setOpacity(opacity);
        }
        setVisibilityLayer(l.id, Boolean(opacity));
      });
      return [...old];
    });
  }, [setVisibilityLayer]);

  /**
   * Inicializa o mapa do leaflet com as configurações do módulo átivo
   * @returns
   */
  const initializeMap = useCallback(async () => {
    setLoadingMap(true);
    // Configurações iniciais padrão para caso não tenha sido informado nas configurações
    const defaultConfig = {
      disableMove: '0',
      hideBaseMap: '0',
      maxLong: '-36.3258037',
      maxLat: '-7.5326541',
      minLong: '-46.6370000',
      minLat: '-19.3422499',
      centerLong: '-41.7210725',
      centerLat: '-12.4723107',
      initialZoom: '6',
      minLimitZoom: '0',
      maxLimitZoom: '20',
    };

    // Armazena as configurações de mapa do módulo
    const mapTool = getElement('CPN-MA-MAP');
    const moduleConfig = mapTool?.configuracao || defaultConfig;

    let initMap = L.map('map', {
      center: [
        moduleConfig?.centerLat?.replace(',', '.') || defaultConfig.centerLat,
        moduleConfig?.centerLong?.replace(',', '.') || defaultConfig.centerLong,
      ],
      zoom: moduleConfig?.initialZoom || defaultConfig.initialZoom,
      zoomControl: false,
      dragging:
        (moduleConfig?.disableMove || defaultConfig.disableMove) === '0',
      minZoom: moduleConfig?.minLimitZoom || defaultConfig.minLimitZoom,
      maxZoom: moduleConfig?.maxLimitZoom || defaultConfig.maxLimitZoom,
      maxBounds: [
        [
          moduleConfig?.minLat?.replace(',', '.') || defaultConfig.minLat,
          moduleConfig?.minLong?.replace(',', '.') || defaultConfig.minLong,
        ],
        [
          moduleConfig?.maxLat?.replace(',', '.') || defaultConfig.maxLat,
          moduleConfig?.maxLong?.replace(',', '.') || defaultConfig.maxLong,
        ],
      ],
      preferCanvas: true,
    });

    new L.Control.Zoom({ position: 'topright' }).addTo(initMap);

    initMap.pm.setLang('pt_br');
    initMap.pm.setGlobalOptions({
      tooltips: false,
    });

    // Criando Pane para usar para todas as camadas adicionadas
    const mixedPane = initMap.createPane('mixedPane');
    mixedPane.style.zIndex = 300;

    // Criando Pane para usar para 'clonar' feições de camadas 'internas' (WMS)
    initMap.createPane('paneClonedSelectedFeatures');
    initMap.getPane('paneClonedSelectedFeatures').style.zIndex = 350;

    /** =================================
     * ========== MAPAS BASE ============
     * *** Adicionando os mapas bases ***
     ================================= */

    const basemapsOfProvider = [];
    // * Captura baseMap principal
    if (mapTool.configuracao?.mainBasemap) {
      const result = await addBaseMap(mapTool.configuracao.mainBasemap, true);
      if (result) {
        result.layer.addTo(initMap);
        basemapsOfProvider.push(result);
      }
    }

    // * Captura demais basemaps
    if (mapTool.configuracao?.basemaps?.length > 0) {
      const basemapsAvailable = await basemapsCrud.get();
      const idsBasemapsAvailable = basemapsAvailable.data._embedded.mapa_base.map((basemap) => basemap.id.toString());
      const basemapsPromises = mapTool.configuracao.basemaps.map(async (b) => {
        if (b !== mapTool.configuracao.mainBasemap.toString() && idsBasemapsAvailable.includes(b)) {
          const result = await addBaseMap(b, false);
          result && basemapsOfProvider.push(result);
          return result;
        }
      });
      await Promise.all(basemapsPromises);
      basemapsOfProvider.sort((a, b) => {
        if (a.titulo < b.titulo) {
          return -1;
        }
        if (a.titulo > b.titulo) {
          return 1;
        }
        return 0;
      });
    }
    setBasemaps(basemapsOfProvider);
    activeModule.basemaps = basemapsOfProvider

    /** ================================================
     * ========== DISPONIBILIDADE GEOSERVER ============
     * *** Verifica a disponibilidade do serviço de ****
     * *** camadas WMS nativas do Geobahia.  ***
      ================================================= */

    const checkGeoserverAvailability = async () => {
      try {
        await WMS.getCapabilities(`${process.env.REACT_APP_GEO_SERVER_HOST}/wms`);
      } catch (error) {
        ReactDOM.render(
          <AlertConfirm
            title='Aviso'
            titleColor="#EEB595"
            buttonStyle={{ width: '100px', marginTop: '30px' }}
            message='As camadas nativas do Geobahia estão temporariamente indisponíveis.'
            buttonText='Ok'
          />,
          document.getElementById('alert')
        );
        return [];
      }
    };
    map.current = initMap;

    /** =======================================
     * ========== CAMADAS INICIAIS ============
     * *** Adicionanado as camadas iniciais ***
     ======================================= */

    let sortedLayers = [];
    if (activeModule.camadas?.camadas?.length > 0) {
      const initialLayers = activeModule.camadas.camadas.filter((l) => l.isInitial()); // Filtra camadas iniciais
      const layersArray = await Promise.all(initialLayers.map(async (l) => addLayer(l, initMap))); // Adiciona camadas iniciais
      sortedLayers = sortLayers(layersArray.filter(Boolean)); // Remove camadas nulas          
    }
    setLayers(sortedLayers);
    setLoadingMap(false);

    checkGeoserverAvailability();
  }, [activeModule, addBaseMap, addLayer, getElement, sortLayers]);



  /**
   * Reseta o mapa, camadas e mapas-base
   */
  const resetMap = useCallback(() => {
    map.current?.off();
    map.current?.remove();
    map.current = null;
    setLayers([]);
    setBasemaps([]);
  }, []);

  /**
   * Reorganiza os z-index das camadas ao atualizar as layers
   */
  useEffect(() => {
    organizeLayersZIndex(layers);
  }, [organizeLayersZIndex, layers]);

  return (
    <Context.Provider
      value={{
        loadingMap,
        loadingInMap,
        setLoadingInMap,
        map: map.current,
        layers,
        basemaps,
        setLayers,
        initializeMap,
        resetMap,
        addBaseMap,
        changeBaseMap,
        changeBaseMapDinamic,
        changeAttributionDinamic,
        enableSideBySide,
        disableSideBySide,
        changeSide,
        addLayer,
        addRasterLayer,
        removeLayer,
        hideLayer,
        changeLabelOpacity,
        changeStatusLabelVisible,
        changeSelectedLabelColumn,
        configureVisibilityLabel,
        showLayer,
        setVisibilityLayer,
        changeLayerOpacity,
        applyLayerOpacity,
        organizeLayersZIndex,
        sortLayers,
        highlightSelectedFeature,
        removeClonedHighlightLayer,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export function useMap() {
  const context = useContext(Context);

  if (!context) {
    throw new Error('useMap must be used within an MapProvider');
  }
  return context;
}

export function withMap(WrappedComponent) {
  return (props) => {
    const context = useContext(Context);

    if (!context) {
      throw new Error('withMap must be used within an MapProvider');
    }
    return <WrappedComponent {...props} mapContext={context} />;
  };
}
